/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Typescript component handles XLSX extension based file selection & parse to JSON then sends to
 * the backend server to store route data.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  InputLabel,
  Modal,
  Paper,
  Popover,
  Typography,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { format } from "date-fns";
import { ChangeEvent, useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import agent from "../../app/api/agent";
import getAxiosError from "../../app/api/axiosError";
import { getDdClientsList } from "../../app/features/client/clientSlice";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { DdRouteDashboardDto } from "../../app/models/responseModels/DdRouteResponse";
import Loader from "../../components/common/loader/Loader";
import { useSubmitModalWithParameter } from "../../components/common/modalPopup/useSubmitModal";
import { IMPORT_FILE_HEADERS } from "./DdTypes";

/** Wrapper type to get parameter for this function */
interface Args {
  options: string[];
  value: string;
  source: string;
}

/** Wrapper type to get parameter for this function */
interface ImportRouteDataArgs {
  clientId: number;
}

interface RouteImportType {
  [key: string]: {
    optionToShow: string;
    source: string;
    selectedField: string;
  };
}

export const ImportRouteData = (props: ImportRouteDataArgs) => {
  const routeImportFields: RouteImportType = {
    Date: {
      optionToShow: "Date",
      source: "Date",
      selectedField: "NA",
    },
    Store: {
      optionToShow: "Store",
      source: "Store",
      selectedField: "NA",
    },
    "Trip ID": {
      optionToShow: "Trip ID",
      source: "Trip ID",
      selectedField: "NA",
    },
    "Driver User Id": {
      optionToShow: "Van ID", //Driver User Id
      source: "Driver User Id",
      selectedField: "NA",
    },
    "Driver Request": {
      optionToShow: "Driver Request",
      source: "Driver Request",
      selectedField: "NA",
    },
    "Enroute to Pickup": {
      optionToShow: "Enroute to Pickup",
      source: "Enroute to Pickup",
      selectedField: "NA",
    },
    "On Time Enroute to Store": {
      optionToShow: "On Time Enroute to Store",
      source: "On Time Enroute to Store",
      selectedField: "NA",
    },
    "At Pickup": {
      optionToShow: "At Pickup",
      source: "At Pickup",
      selectedField: "NA",
    },
    "On Time Arrival": {
      optionToShow: "On Time Arrival",
      source: "On Time Arrival",
      selectedField: "NA",
    },
    "Associate Dispense": {
      optionToShow: "Associate Dispense",
      source: "Associate Dispense",
      selectedField: "NA",
    },
    "Load Start": {
      optionToShow: "Load Start",
      source: "Load Start",
      selectedField: "NA",
    },
    "Driver Dwell Time": {
      optionToShow: "Driver Dwell Time",
      source: "Driver Dwell Time",
      selectedField: "NA",
    },
    "Load End": {
      optionToShow: "Load End",
      source: "Load End",
      selectedField: "NA",
    },
    "Driver Load Time (min)": {
      optionToShow: "Driver Load Time (min)",
      source: "Driver Load Time (min)",
      selectedField: "NA",
    },
    Departure: {
      optionToShow: "Departure",
      source: "Departure",
      selectedField: "NA",
    },
    "On Time Departure": {
      optionToShow: "On Time Departure",
      source: "On Time Departure",
      selectedField: "NA",
    },
    "Driver Last Delivery": {
      optionToShow: "Driver Last Delivery",
      source: "Driver Last Delivery",
      selectedField: "NA",
    },
    "Trip Actual Time (min)": {
      optionToShow: "Trip Actual Time (min)",
      source: "Trip Actual Time (min)",
      selectedField: "NA",
    },
    "Driver Doorstep Time (min)": {
      optionToShow: "Driver Doorstep Time (min)",
      source: "Driver Doorstep Time (min)",
      selectedField: "NA",
    },
    "Enroute to Return": {
      optionToShow: "Enroute to Return",
      source: "Enroute to Return",
      selectedField: "NA",
    },
    "At Return": {
      optionToShow: "At Return",
      source: "At Return",
      selectedField: "NA",
    },
    "Trip Marked Complete": {
      optionToShow: "Trip Marked Complete",
      source: "Trip Marked Complete",
      selectedField: "NA",
    },
    "Trip Planned Start": {
      optionToShow: "Trip Planned Start",
      source: "Trip Planned Start",
      selectedField: "NA",
    },
    "Orders - Total": {
      optionToShow: "Orders - Total",
      source: "Orders - Total",
      selectedField: "NA",
    },
    "Orders - Delivered": {
      optionToShow: "Orders - Delivered",
      source: "Orders - Delivered",
      selectedField: "NA",
    },
    "Orders - Undelivered": {
      optionToShow: "Orders - Undelivered",
      source: "Orders - Undelivered",
      selectedField: "NA",
    },
    "Orders - Undelivered - No Attempt": {
      optionToShow: "Orders - Undelivered - No Attempt",
      source: "Orders - Undelivered - No Attempt",
      selectedField: "NA",
    },
    "Orders - Undelivered - Attempted": {
      optionToShow: "Orders - Undelivered - Attempted",
      source: "Orders - Undelivered - Attempted",
      selectedField: "NA",
    },
    Miles: {
      optionToShow: "Miles",
      source: "Miles",
      selectedField: "NA",
    },
    "Units - Total": {
      optionToShow: "Units - Total",
      source: "Units - Total",
      selectedField: "NA",
    },
    "Units - Orders With Units": {
      optionToShow: "Units - Orders with Units",
      source: "Units - Orders With Units",
      selectedField: "NA",
    },
  };

  // selectedField state
  const [routeImportFieldsState, setRouteImportFieldsState] =
    useState<RouteImportType>(routeImportFields);

  //required headers expected in a file
  const headersReq = IMPORT_FILE_HEADERS;

  //holds reference of the hidden input type file element for file selection
  const inputFileRef = useRef<HTMLInputElement>(null);

  //holds reference of the button element to import
  const importBtnRef = useRef<HTMLButtonElement>(null);

  // state to anchor popover on particular element
  const [anchorEl, setAnchorEl] = useState<any>(null);

  const dispatch = useAppDispatch();

  // point to the client data from Store
  const { clientList } = useSelector((state: RootState) => state.client);

  // authenticated user information from store
  const { authResponse } = useSelector((state: RootState) => state.login);

  // state to manage client selection popover incase user is in landing page
  const [openClientSelectionModal, setOpenClientSelectionModal] =
    useState<boolean>(false);

  // state to display modal
  const [openModal, setOpenModal] = useState<boolean>(false);

  // state to manage clientId after selected inside popover
  const [clientId, setClientId] = useState<any>(props.clientId);

  // state to display loader when db communication is on progress
  const [dbInteractionOnProgress, setDbInteractionOnProgress] = useState(false);

  // state to hold available header after parse from file
  const [fileParsedHeader, setFileParsedHeader] = useState<any[]>([]);

  // state to hold data after parse from file
  const [fileParsedData, setFileParsedData] = useState<any[]>([]);

  // state to hold cloned data after parse from file
  const [fileParsedClonedData, setFileParsedClonedData] = useState<any[]>([]);

  const [fieldDataType, setFieldDataType] = useState<{
    [key: string]: string;
  }>({
    Date: "date",
    "Trip Marked Complete": "datetime",
    "At Return": "datetime",
    "Enroute to Return": "datetime",
    "Driver Last Delivery": "datetime",
    "Trip Planned Start": "datetime",
    Departure: "datetime",
    "Load End": "datetime",
    "Load Start": "datetime",
    "At Pickup": "datetime",
    "Associate Dispense": "datetime",
    "Driver Request": "datetime",
    "Enroute to Pickup": "datetime",
  });

  // fetch all dedicated delivery enabled client
  const getAllDdClients = useCallback(async () => {
    try {
      setDbInteractionOnProgress(true);
      // make a call to server to get details
      const response = await dispatch(getDdClientsList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "storeData",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "storeDataException",
      });
    } finally {
      setDbInteractionOnProgress(false);
    }
  }, [dispatch]);

  // Persist route call to server through reducer
  const storeRoute = useCallback(
    async (routesRaw: DdRouteDashboardDto[]) => {
      try {
        // before making call to bd enable loader on the page
        setDbInteractionOnProgress(true);
        // dispatch returned data from backend end call after persisting routes
        agent.DedicatedDeliveryAgent.storeRouteData({
          routes: routesRaw.filter((obj) => {
            const keys = Object.keys(obj);
            if (keys.length === 0) {
              return false; // Filter if object has no keys
            } else if (
              keys.length === 1 &&
              keys[0].toLocaleLowerCase() === "date" &&
              (obj.Date === "1900-01-00" || !obj.Date)
            ) {
              return false; // Filter if object has only one key "Date" with value "1900-01-00"
            } else {
              return true; // Keep the object otherwise
            }
          }),
          clientId: clientId,
          createdBy: authResponse?.userSettings?.userName ?? "",
          updatedBy: authResponse?.userSettings?.userName ?? "",
        })
          .then((response) => {
            console.log(response);
            // if response is not success
            if (!response.isSuccess) {
              toast.error(response.message!, {
                toastId: "storeData",
              });
            }
            try {
              // hide modal after backend call
              setOpenModal(false);
              // process data received from server
              if (Object.keys(response.data).length) {
                let data = response.data;
                console.log(data);
                //convert json to excel sheet
                const worksheet = XLSX.utils.json_to_sheet(data);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet);
                XLSX.writeFile(
                  workbook,
                  "error_response" +
                    format(new Date(), "yyyyMMddHHmmss") +
                    ".xlsx",
                  {
                    compression: true,
                  }
                );
                // show warning message to user mentioning while importing file few data got rejected
                toast.warning(
                  "File has been processed having few error, please refer the error file."
                );
                // show success message to user in case of empty response from server
              } else {
                toast.success("File has been processed successfully.");
              }
            } catch (e) {}
          })
          .catch((error) => {
            console.log(error);
            routesRaw.map((e) => {
              return (e.Reason = error.message.message
                ? error.message.message
                : error.message.error);
            });
            const worksheet = XLSX.utils.json_to_sheet(routesRaw);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet);
            XLSX.writeFile(
              workbook,
              "error_response" + format(new Date(), "yyyyMMddHHmmss") + ".xlsx",
              {
                compression: true,
              }
            );
          });
      } catch (error: any) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: "storeDataException",
        });
      } finally {
        clearAllData();
      }
    },
    [dispatch, clientId]
  );

  const resetSelectedFields = () => {
    const updatedFields = { ...routeImportFieldsState };
    Object.keys(updatedFields).forEach((key) => {
      updatedFields[key].selectedField = "NA";
    });
    setRouteImportFieldsState(updatedFields);
  };

  const clearAllData = () => {
    setDbInteractionOnProgress(false);
    setFileParsedData([]);
    setFileParsedClonedData([]);
    setFileParsedHeader([]);
    setOpenModal(false);
    resetSelectedFields();
  };

  const parseNumberToDate = (
    value: number,
    type: "date" | "datetime"
  ): string | number | undefined => {
    const dateObj = XLSX.SSF.parse_date_code(value);
    if (dateObj) {
      const { y, m, d, H, M, S } = dateObj;
      const year = y;
      const month = m;
      const day = d;
      const hour = H;
      const minute = M;
      const second = S;
      const formattedHour = hour < 10 ? "0" + hour : hour;
      const formattedMinute = minute < 10 ? "0" + minute : minute;
      const formattedSecond = second < 10 ? "0" + second : second;

      if (type === "date") {
        const yyyyMmDd = `${year}-${month < 10 ? "0" + month : month}-${
          day < 10 ? "0" + day : day
        }`;
        return yyyyMmDd.startsWith("1900") ? undefined : yyyyMmDd;
      } else if (type === "datetime") {
        const yyyyMmDdHhMmSs = `${year}-${month < 10 ? "0" + month : month}-${
          day < 10 ? "0" + day : day
        } ${formattedHour}:${formattedMinute}:${formattedSecond}`;
        return yyyyMmDdHhMmSs.startsWith("1900") ? value : yyyyMmDdHhMmSs;
      }
    }
    return undefined;
  };

  // function to handle file upload
  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    try {
      // if incase no file has selected then show error message
      if (!e.target.files || e.target.files.length === 0) {
        toast.error("Please select a file.", {
          toastId: "fileSelectException",
        });
        return;
      }
      // read file using FileReader
      const fileReader = new FileReader();
      fileReader.readAsBinaryString(e.target.files[0]);
      fileReader.onload = (f) => {
        const data = f.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetname = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetname];
        const parsedData = XLSX.utils.sheet_to_json<any[]>(sheet, {
          header: 1,
        });
        // convert numeric digit of date to actual date for server
        parsedData.map((row, rowIndex) => {
          if (rowIndex !== 0) {
            // handle special case for Date field basically parses data values into a numeric digit
            const dateIdx = parsedData[0].indexOf("Date");
            const tripMarkedCompIdx = parsedData[0].indexOf(
              "Trip Marked Complete"
            );
            const atReturnIdx = parsedData[0].indexOf("At Return");
            const enrouteToReturnIdx =
              parsedData[0].indexOf("Enroute to Return");
            const driverLastDeliveryIdx = parsedData[0].indexOf(
              "Driver Last Delivery"
            );
            const tripPlannedIdx = parsedData[0].indexOf("Trip Planned Start");
            const departureIdx = parsedData[0].indexOf("Departure");
            const loadEndIdx = parsedData[0].indexOf("Load End");
            const loadStartIdx = parsedData[0].indexOf("Load Start");
            const atPickupIdx = parsedData[0].indexOf("At Pickup");
            const associateDispenseIdx =
              parsedData[0].indexOf("Associate Dispense");
            const driverRequestIdx = parsedData[0].indexOf("Driver Request");
            const enrouteToPickupIdx =
              parsedData[0].indexOf("Enroute to Pickup");

            if (typeof parsedData[rowIndex][dateIdx] === "number") {
              row[dateIdx] = parseNumberToDate(
                parsedData[rowIndex][dateIdx],
                "date"
              );
            }
            if (typeof parsedData[rowIndex][tripMarkedCompIdx] === "number") {
              row[tripMarkedCompIdx] = parseNumberToDate(
                parsedData[rowIndex][tripMarkedCompIdx],
                "datetime"
              );
            }
            if (typeof parsedData[rowIndex][atReturnIdx] === "number") {
              row[atReturnIdx] = parseNumberToDate(
                parsedData[rowIndex][atReturnIdx],
                "datetime"
              );
            }

            if (typeof parsedData[rowIndex][enrouteToReturnIdx] === "number") {
              row[enrouteToReturnIdx] = parseNumberToDate(
                parsedData[rowIndex][enrouteToReturnIdx],
                "datetime"
              );
            }

            if (
              typeof parsedData[rowIndex][driverLastDeliveryIdx] === "number"
            ) {
              row[driverLastDeliveryIdx] = parseNumberToDate(
                parsedData[rowIndex][driverLastDeliveryIdx],
                "datetime"
              );
            }

            if (typeof parsedData[rowIndex][tripPlannedIdx] === "number") {
              row[tripPlannedIdx] = parseNumberToDate(
                parsedData[rowIndex][tripPlannedIdx],
                "datetime"
              );
            }

            if (typeof parsedData[rowIndex][departureIdx] === "number") {
              row[departureIdx] = parseNumberToDate(
                parsedData[rowIndex][departureIdx],
                "datetime"
              );
            }

            if (typeof parsedData[rowIndex][loadEndIdx] === "number") {
              row[loadEndIdx] = parseNumberToDate(
                parsedData[rowIndex][loadEndIdx],
                "datetime"
              );
            }

            if (typeof parsedData[rowIndex][loadStartIdx] === "number") {
              row[loadStartIdx] = parseNumberToDate(
                parsedData[rowIndex][loadStartIdx],
                "datetime"
              );
            }

            if (typeof parsedData[rowIndex][atPickupIdx] === "number") {
              row[atPickupIdx] = parseNumberToDate(
                parsedData[rowIndex][atPickupIdx],
                "datetime"
              );
            }

            if (
              typeof parsedData[rowIndex][associateDispenseIdx] === "number"
            ) {
              row[associateDispenseIdx] = parseNumberToDate(
                parsedData[rowIndex][associateDispenseIdx],
                "datetime"
              );
            }

            if (typeof parsedData[rowIndex][driverRequestIdx] === "number") {
              row[driverRequestIdx] = parseNumberToDate(
                parsedData[rowIndex][driverRequestIdx],
                "datetime"
              );
            }

            if (typeof parsedData[rowIndex][enrouteToPickupIdx] === "number") {
              row[enrouteToPickupIdx] = parseNumberToDate(
                parsedData[rowIndex][enrouteToPickupIdx],
                "datetime"
              );
            }
            console.log(row[dateIdx]);
          }
          return row;
        });
        // convert excel format of json to key value pair Object[]
        if (parsedData.length >= 2) {
          const headers = parsedData[0];
          setFileParsedHeader(headers);
          handleAutoSelectValue(headers);
          let newMappedData: any[] = [];
          let index = 0;
          parsedData.forEach((each, idx) => {
            if (idx >= 1) {
              let obj: Record<string, any> = {};
              for (let count = 0; count < headers.length; count++) {
                obj["" + headers[count] + ""] = each[count];
              }
              newMappedData[index++] = obj;
            }
          });
          // set parsed data to state
          setFileParsedData(newMappedData);
          // set a clone of parsed data
          setFileParsedClonedData(JSON.parse(JSON.stringify(newMappedData)));
          // open modal for user confirmation of parsed headers
          setOpenModal(true);
        } else {
          toast.error("Either file not contain header or data");
        }
      };
    } catch (e) {
    } finally {
      e.target.value = "";
    }
  };

  const handleCustomFieldSelection = (
    field: string,
    newSelectedField: string
  ) => {
    const updatedFields = { ...routeImportFieldsState };
    updatedFields[field].selectedField = newSelectedField;
    setRouteImportFieldsState(updatedFields);
  };

  const handleAutoSelectValue = (fileParsedHeader: any[]) => {
    const updatedFields = { ...routeImportFieldsState };
    Object.entries(routeImportFieldsState).forEach(([key, value]) => {
      const matchedValue = fileParsedHeader.find(
        (f: string) => value.optionToShow === f
      );
      if (matchedValue) {
        updatedFields[key].selectedField = matchedValue;
      }
    });
    setRouteImportFieldsState(updatedFields);
  };

  const ImportHeaderSelection = (args: Args) => {
    return (
      <FormControl size="small" fullWidth>
        <Select
          style={{
            border: !args.value || args.value === "NA" ? "1px solid red" : "",
          }}
          defaultValue={!args.value ? "NA" : args.value}
          name={args.source}
          onChange={(e, data) => {
            if (e.target.value && e.target.value !== "NA") {
              const field = e.target.name;
              const selectedField = e.target.value;
              console.log("source: ", field, "value: ", selectedField);
              console.log(routeImportFieldsState);

              handleCustomFieldSelection(field, selectedField);
              const updatedData = fileParsedClonedData.map((f, idx) => {
                let newValue = fileParsedData[idx][selectedField];
                const typeOfField = fieldDataType[field];
                if (typeOfField === "date" || typeOfField === "datetime") {
                  newValue = parseNumberToDate(newValue, typeOfField);
                }
                return { ...f, [field]: newValue };
              });

              if (updatedData) {
                setFileParsedClonedData(updatedData);
              }
              console.log(updatedData, fileParsedData);
            }
          }}
        >
          <MenuItem value="NA">No Match Found</MenuItem>
          {args.options.map((e) => {
            return (
              <MenuItem value={e} key={e}>
                {e}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  };

  const [routesByStoreModal, openRoutesByStoreModal] =
    useSubmitModalWithParameter<any>((rowData, closeRoutesByStoreModal) => (
      <Popover
        id="select-client"
        open={openClientSelectionModal}
        anchorEl={anchorEl}
        onClose={() => {
          setOpenClientSelectionModal(false);
          setClientId("");
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <FormControl sx={{ m: 1, minWidth: 150 }} size="small">
          <InputLabel id="select-small-label">Select Client</InputLabel>
          <Select
            labelId="select-small-label"
            id="select-small"
            label="Select Client"
            onChange={(e) => {
              if (e.target.value !== 0) {
                setClientId(e.target.value);
              }
              console.log(e.target.value);
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {clientList.map((e, indx) => {
              return (
                <MenuItem value={e.id} key={(e.clientName + indx).toString()}>
                  {e.clientName}
                </MenuItem>
              );
            })}
          </Select>
          <Button
            disabled={!clientId || clientId == -1 ? true : false}
            onClick={(e) => {
              inputFileRef.current?.click();
              setOpenClientSelectionModal(false);
            }}
          >
            Submit
          </Button>
        </FormControl>
      </Popover>
    ));

  return (
    <>
      <Loader isLoading={dbInteractionOnProgress} />
      <input
        accept=".csv, .xlsx"
        ref={inputFileRef}
        id="icon-button-video"
        onChange={(e) => handleFileUpload(e)}
        type="file"
        style={{ display: "none" }}
      />
      <label htmlFor="icon-button-video">
        <Button
          fullWidth
          size="medium"
          variant="contained"
          color="success"
          ref={importBtnRef}
          endIcon={<FileUploadOutlinedIcon />}
          onClick={(e) => {
            const path_name = window.location.pathname;
            console.log(window.location.pathname);
            if (path_name !== "/dedicated-delivery") {
              inputFileRef.current?.click();
            } else {
              openRoutesByStoreModal("");
              getAllDdClients();
              setOpenClientSelectionModal(true);
              setAnchorEl(importBtnRef.current);
            }
          }}
        >
          import
        </Button>
      </label>
      {routesByStoreModal}
      {openModal && (
        <Modal
          aria-labelledby="new-store-add-modal"
          aria-describedby="new-store-add-modal-description"
          open={openModal}
          onClose={(e) => {
            setOpenModal(false);
            clearAllData();
          }}
          style={{ overflow: "scroll" }}
        >
          <Container component={Paper} style={{ marginTop: "2%" }}>
            <Grid container paddingTop={1} rowSpacing={2}>
              <Grid
                container
                item
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  aria-label="modal-title"
                  variant="h4"
                  component="h1"
                >
                  Import from file
                </Typography>
                <IconButton
                  onClick={() => {
                    setOpenModal(false);
                  }}
                >
                  <CloseRoundedIcon sx={{ color: "primary.secondary" }} />
                </IconButton>
              </Grid>
              {Object.entries(routeImportFieldsState).map(
                ([key, value], indx) => (
                  <Grid
                    container
                    item
                    alignItems="center"
                    key={(key + indx).toString()}
                  >
                    <Grid
                      item
                      xs={3}
                      justifyContent="flex-end"
                      paddingRight={2}
                    >
                      <Box display="flex" justifyContent="flex-end">
                        <Typography variant="subtitle2" component="p">
                          {key}:{" "}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={9}>
                      <ImportHeaderSelection
                        options={fileParsedHeader.filter(
                          (e) =>
                            e === value.optionToShow ||
                            headersReq.indexOf(e) === -1
                        )}
                        value={value.selectedField}
                        source={value.source}
                      />
                    </Grid>
                  </Grid>
                )
              )}
              <Grid
                container
                item
                justifyContent="center"
                alignItems="center"
                marginBottom={2}
              >
                <Button
                  fullWidth
                  color="success"
                  variant="contained"
                  onClick={(e) => {
                    storeRoute(fileParsedClonedData);
                  }}
                >
                  Confirm
                </Button>
              </Grid>
            </Grid>
          </Container>
        </Modal>
      )}
    </>
  );
};
